import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { BookOpen, Building2, FolderOpen, Search, Clock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useKnowledgeBases,
  useCompanyKnowledgeBase,
  useDocuments,
  type KnowledgeBase,
  type Document,
} from '@/hooks'

export const Route = createFileRoute('/_authenticated/knowledge-base/')({
  component: KnowledgeBasePage,
})

/**
 * 知识库首页
 * 显示公司知识库、项目知识库列表和最近文档
 */
function KnowledgeBasePage() {
  const navigate = useNavigate()
  const { data: knowledgeBases, isLoading: kbLoading } = useKnowledgeBases()
  const { data: companyKB } = useCompanyKnowledgeBase()

  // 获取公司知识库的最近文档
  const { data: recentDocs, isLoading: docsLoading } = useDocuments(companyKB?.id ?? '', {
    pageSize: 10,
  })

  const handleOpenSearch = () => {
    void navigate({ to: '/knowledge-base/search' })
  }

  const handleSelectCompanyKB = () => {
    if (companyKB) {
      void navigate({ to: '/knowledge-base/spaces/$spaceId', params: { spaceId: companyKB.id } })
    }
  }

  const handleSelectProjectKB = (kb: KnowledgeBase) => {
    void navigate({ to: '/knowledge-base/spaces/$spaceId', params: { spaceId: kb.id } })
  }

  const handleSelectDocument = (documentId: string) => {
    void navigate({ to: '/knowledge-base/documents/$documentId', params: { documentId } })
  }

  // 分离项目知识库
  const projectKBs = knowledgeBases?.filter((kb) => kb.type === 'project') ?? []

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">知识库</h1>
          <p className="text-muted-foreground">管理和分享团队知识</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleOpenSearch}>
            <Search className="mr-2 h-4 w-4" />
            搜索
          </Button>
        </div>
      </div>


      {/* Company Knowledge Base */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">公司知识库</h2>
        </div>

        {kbLoading ? (
          <Skeleton className="h-24" />
        ) : companyKB ? (
          <KnowledgeBaseCard
            knowledgeBase={companyKB}
            onClick={handleSelectCompanyKB}
            icon={<Building2 className="h-6 w-6 text-primary" />}
          />
        ) : (
          <EmptyState
            icon={Building2}
            title="公司知识库未创建"
            description="联系管理员创建公司知识库"
          />
        )}
      </section>

      {/* Project Knowledge Bases */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">项目知识库</h2>
        </div>

        {kbLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : projectKBs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projectKBs.map((kb) => (
              <KnowledgeBaseCard
                key={kb.id}
                knowledgeBase={kb}
                onClick={() => handleSelectProjectKB(kb)}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={FolderOpen}
            title="暂无项目知识库"
            description="加入项目后可访问项目知识库"
          />
        )}
      </section>

      {/* Recent Documents */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">最近文档</h2>
        </div>

        {docsLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : recentDocs && recentDocs.data.length > 0 ? (
          <div className="rounded-lg border divide-y">
            {recentDocs.data.map((doc) => (
              <DocumentRow
                key={doc.id}
                document={doc}
                onClick={() => handleSelectDocument(doc.id)}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={BookOpen}
            title="暂无文档"
            description="在知识库中创建你的第一个文档"
          />
        )}
      </section>
    </div>
  )
}

// ==================== Sub Components ====================


interface KnowledgeBaseCardProps {
  knowledgeBase: KnowledgeBase
  onClick: () => void
  icon?: React.ReactNode
}

function KnowledgeBaseCard({ knowledgeBase, onClick, icon }: KnowledgeBaseCardProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-left w-full"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-2xl">
        {icon ?? <FolderOpen className="h-6 w-6 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium truncate">{knowledgeBase.name}</h3>
        {knowledgeBase.description && (
          <p className="text-sm text-muted-foreground truncate">{knowledgeBase.description}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground">
            {knowledgeBase.type === 'company' ? '公司' : knowledgeBase.project?.name ?? '项目'}
          </span>
          {knowledgeBase.permission && (
            <span className="text-xs text-muted-foreground">
              · {getPermissionLabel(knowledgeBase.permission)}
            </span>
          )}
          {knowledgeBase.documentCount !== undefined && (
            <span className="text-xs text-muted-foreground">
              · {knowledgeBase.documentCount} 篇文档
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

interface DocumentRowProps {
  document: Document
  onClick: () => void
}

function DocumentRow({ document, onClick }: DocumentRowProps) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors text-left"
    >
      <BookOpen className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <h4 className="font-medium truncate">{document.title}</h4>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>{document.author?.name ?? '未知作者'}</span>
          <span>·</span>
          <span>
            {document.updatedAt
              ? `更新于 ${formatDate(document.updatedAt)}`
              : `创建于 ${formatDate(document.createdAt)}`}
          </span>
        </div>
      </div>
    </button>
  )
}

interface EmptyStateProps {
  icon: typeof Clock
  title: string
  description: string
  action?: React.ReactNode
}

function EmptyState({ icon: Icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="font-medium">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1">{description}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

// ==================== Helpers ====================

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000))

  if (diffDays === 0) {
    return '今天'
  } else if (diffDays === 1) {
    return '昨天'
  } else if (diffDays < 7) {
    return `${diffDays} 天前`
  } else {
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric',
    })
  }
}

function getPermissionLabel(permission: string): string {
  switch (permission) {
    case 'admin':
      return '管理员'
    case 'editor':
      return '编辑者'
    case 'viewer':
      return '查看者'
    default:
      return ''
  }
}
